import { readMapping, readMappingPage, readNotificationProductPage } from "../utils/productNotificationContracts";
import httpService from "./httpService";
import type {
  NotificationPortfolio,
  ProductNotificationMappingView,
  ProductNotificationPage,
  ProductNotificationSelections,
  ProductNotificationTarget,
} from "../types/productNotificationMappings";

const ACTOR_AUTH = { requiresAuth: true, requestScope: "actor" as const, cache: "no-store" as const };
const userPath = (userId: string) => `/v3/admin/users/${encodeURIComponent(userId)}/product-notification-mappings`;

class AdminProductNotificationMappingsService {
  async list(userId: string, page = 1, signal?: AbortSignal): Promise<ProductNotificationPage<ProductNotificationMappingView>> {
    const response = await httpService.get<ProductNotificationPage<ProductNotificationMappingView>>(
      `${userPath(userId)}?page=${page}&pageSize=25&includeBaskets=true`, { ...ACTOR_AUTH, signal }
    );
    return readMappingPage(response.data);
  }

  async eligiblePortfolios(userId: string, page = 1, search = "", signal?: AbortSignal): Promise<ProductNotificationPage<NotificationPortfolio>> {
    const query = new URLSearchParams({ page: String(page), pageSize: "25", includeBaskets: "true" });
    if (search.trim()) query.set("search", search.trim());
    const response = await httpService.get<ProductNotificationPage<NotificationPortfolio>>(
      `${userPath(userId)}/eligible-products?${query}`, { ...ACTOR_AUTH, signal }
    );
    return readNotificationProductPage(response.data);
  }

  async upsert(userId: string, productId: number, selections: ProductNotificationSelections, productType: ProductNotificationTarget["productType"] = "PORTFOLIO"): Promise<ProductNotificationMappingView> {
    // Whitelist the four editable channels. Never forward user IDs, metadata,
    // SMS, UI query parameters, or fields from a response object into a write.
    const { shouldEmail, shouldWebInbox, shouldMobileInbox, shouldPush } = selections;
    const response = await httpService.put<ProductNotificationMappingView>(
      `${userPath(userId)}/${productType}/${productId}`,
      { shouldEmail, shouldWebInbox, shouldMobileInbox, shouldPush }, ACTOR_AUTH
    );
    return readMapping(response.data, { productType, productId });
  }

  async remove(userId: string, productId: number, productType: ProductNotificationTarget["productType"] = "PORTFOLIO"): Promise<void> {
    await httpService.delete<unknown>(`${userPath(userId)}/${productType}/${productId}`, ACTOR_AUTH);
  }
}

export default new AdminProductNotificationMappingsService();
